import useAppDispatch from '../../hooks/useAppDispatch';
import { useTranslation } from '../../hooks/useTranslation';
import type { IconId } from '../icon/icons';
import ChatClickableButton from './ChatClickableButton';
import './AssistantChatMessageButton.less';
import type { AssistantChatMessageContent } from '@public-api-contracts/models/chat/ChatMessage';
import useAppSelector from '../../hooks/useAppSelector';
import Chip from '../chip/Chip';

export type AssistantChatMessageButtonProps = {
    content: Extract<AssistantChatMessageContent, { contentType: 'button' }>;
};

const AssistantChatMessageButton = ({
    content
}: AssistantChatMessageButtonProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const mainFeatureItem = useAppSelector(
        state => state.featureState.mainFeatureItem
    );

    const buttonClickHandler = () => {
        if (content.contentType !== 'button') {
            return;
        }
        switch (content.featureType) {
            case 'product-search-similar':
                void dispatch.featureState.startFeature({
                    featureType: 'product-search-similar',
                    baseProductsIds: content.contextualProductIds
                });
                break;
            case 'product-search-combinations':
                void dispatch.featureState.startFeature({
                    featureType: 'product-search-combinations',
                    baseProductsIds: content.contextualProductIds
                });
                break;
            case 'product-mix-and-match':
                void dispatch.featureState.startFeature({
                    featureType: 'product-mix-and-match',
                    productId: content.contextualProductIds[0] ?? ''
                });
                break;
            case 'product-search-similar-by-image':
            case 'product-search-combinations-by-image':
                if (
                    (mainFeatureItem?.featureType !==
                        'product-search-by-image' &&
                        mainFeatureItem?.featureType !==
                            'product-search-similar-by-image' &&
                        mainFeatureItem?.featureType !==
                            'product-search-combinations-by-image') ||
                    mainFeatureItem.loadingStatus !== 'success'
                ) {
                    return;
                }
                dispatch.featureState.removeProductSearchByImageMessage();
                void dispatch.featureState.startFeature({
                    featureType: content.featureType,
                    imageId: mainFeatureItem.imageId,
                    imageUrl: mainFeatureItem.imageUrl,
                    imageDataUrl: mainFeatureItem.imageDataUrl,
                    imageWidth: mainFeatureItem.imageWidth,
                    imageHeight: mainFeatureItem.imageHeight,
                    boundingBoxes: mainFeatureItem.boundingBoxes,
                    selectedBoundingBox: mainFeatureItem.selectedBoundingBox
                });
                break;
            default:
                break;
        }
    };

    const getButtonIconId = (): IconId => {
        if (content.contentType !== 'button') {
            return 'star';
        }
        switch (content.featureType) {
            case 'product-search-similar':
            case 'product-search-similar-by-image':
                return 'similar';
            case 'product-search-combinations':
            case 'product-search-combinations-by-image':
                return 'join';
            case 'product-mix-and-match':
                return 'outfit';
            default:
                return 'star';
        }
    };

    const getBoundingBoxCategory = (): string | undefined => {
        if (!mainFeatureItem || !('selectedBoundingBox' in mainFeatureItem)) {
            return undefined;
        }
        return (
            mainFeatureItem.selectedBoundingBox?.categoryTranslatedValue ??
            mainFeatureItem.selectedBoundingBox?.categoryValue
        );
    };

    const ButtonContent = () => {
        if (content.contentType !== 'button') {
            return '';
        }
        const boundingBoxCategory = getBoundingBoxCategory();
        switch (content.featureType) {
            case 'product-search-similar':
                return t('SUGGESTED_ALTERNATIVE_SEARCH_SIMILAR_BUTTON_LABEL');
            case 'product-search-similar-by-image':
                return boundingBoxCategory ? (
                    <>
                        {t('PRODUCT_SEARCH_CHAT_FEATURE_SIMILAR_TO_CATEGORY')}{' '}
                        <Chip variant="secondary-gray-200">
                            {boundingBoxCategory}
                        </Chip>
                    </>
                ) : (
                    t('PRODUCT_SEARCH_CHAT_FEATURE_SIMILAR_TO_FULL_IMAGE')
                );
            case 'product-search-combinations':
                return t(
                    'SUGGESTED_ALTERNATIVE_SEARCH_COMBINATIONS_BUTTON_LABEL'
                );
            case 'product-search-combinations-by-image':
                return boundingBoxCategory ? (
                    <>
                        {t(
                            'PRODUCT_SEARCH_CHAT_FEATURE_COMBINATIONS_BY_CATEGORY'
                        )}{' '}
                        <Chip variant="secondary-gray-200">
                            {boundingBoxCategory}
                        </Chip>
                    </>
                ) : (
                    t('PRODUCT_SEARCH_CHAT_FEATURE_COMBINATIONS_BY_FULL_IMAGE')
                );
            case 'product-mix-and-match':
                return t('SUGGESTED_ALTERNATIVE_MIX_AND_MATCH_BUTTON_LABEL');
            default:
                return '';
        }
    };

    return (
        <ChatClickableButton
            startIconId={getButtonIconId()}
            onClick={buttonClickHandler}
        >
            <ButtonContent />
        </ChatClickableButton>
    );
};

export default AssistantChatMessageButton;
